import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { EventType } from '@app/shared/constants-server';
import type { RentalSaleCancellation } from '@app/shared/_models/sales';
import type { RentalSaleLineView } from '../rental-billing.model';
import { CLOSED_LINE, lineClosed } from '../rental-billing.labels';
import { RentalDecisionDialogsService } from '../rental-decision-dialogs.service';
import { RentalDecisionEvent, saleCancellationAdd } from '../rental-decision-events';
import type { SaleCancellationDialogData } from '../rental-dialogs.contract';
import { qtyFr } from '../rental-days';
import { RentalEffectComponent } from '../effect/rental-effect.component';
import { ReasonChip, RentalDecisionDialogBase, cancelledText, lineName, reasonChips, stampText } from './rental-decision-dialog.base';

interface Figure { label: string; value: string; }
interface CancellationRow { id: number; what: string; stamp: string; cancelled: string | null; replaces: number | null; live: boolean; }

export const SALE_FSE_BLOCKED = 'Vente déjà facturée sur une FSE : la correction se fait hors moteur';

// Annulation d'une quantité vendue (2106) : le plafond est celui du serveur, jamais recalculé ici (E.8).
@Component({
  selector: 'app-rental-sale-cancel-dialog',
  templateUrl: './rental-sale-cancel-dialog.component.html',
  styleUrls: ['./rental-sale-cancel-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatDialogModule, MatProgressSpinnerModule, RentalEffectComponent],
})
export class RentalSaleCancelDialogComponent extends RentalDecisionDialogBase<SaleCancellationDialogData> {
  protected readonly dialogs = inject(RentalDecisionDialogsService);

  qtyText = '';
  badQty = false;
  reasonId: number | null = null;
  comment = '';

  sale: RentalSaleLineView | null = null;
  name = '';
  figures: Figure[] = [];
  help = '';
  rows: CancellationRow[] = [];
  reasons: ReasonChip[] = [];
  blocked: string | null = null;
  commentRequired = false;
  effectText: string | null = null;
  errors: string[] = [];
  canSave = false;

  constructor() {
    super();
    this.update();
  }

  protected update(): void {
    const s = this.store.sale(this.data.lineId) ?? null;
    this.sale = s;
    if (!this.store.view || !s) {
      this.canSave = false;
      this.plan(null);
      return;
    }
    this.name = lineName(s);
    this.figures = [
      { label: 'Quantité de la ligne', value: qtyFr(s.qty) },
      { label: 'Livré', value: qtyFr(s.due_limit) },
      { label: 'Annulé', value: qtyFr(s.cancelled_qty) },
      { label: 'Facturé', value: qtyFr(s.billed_qty) },
      { label: 'Reste à régler', value: qtyFr(s.to_settle_qty) },
    ];
    this.help = `livré ${qtyFr(s.due_limit)}, déjà annulé ${qtyFr(s.cancelled_qty)}`;
    this.rows = [...(s.cancellations ?? [])].sort((a, b) => a.id - b.id).map((c) => this.rowOf(c));
    this.reasons = reasonChips(this.store, 'SALE_CANCELLATION');
    this.blocked = s.fse_invoiced ? SALE_FSE_BLOCKED : lineClosed(s) ? CLOSED_LINE : null;
    this.commentRequired = this.reasonId !== null && this.store.reasonFlag(this.reasonId, 'comment_required');
    this.errors = this.errorsOf();
    const ev = this.event();
    const q = this.qty();
    this.effectText = this.blocked ? null : ev && q !== null ? `Annulation de ${qtyFr(q)} × ${this.name}` : "Saisissez la quantité annulée et le motif pour voir l'effet.";
    this.canSave = !this.saving && !!ev && this.errors.length === 0;
    this.plan(ev && !this.blocked && !this.errors.length ? [ev] : null);
  }

  private rowOf(c: RentalSaleCancellation): CancellationRow {
    const comment = c.comment?.trim() ? ` · « ${c.comment.trim()} »` : '';
    return {
      id: c.id,
      what: `n° ${c.id} · ${qtyFr(c.qty)} annulé(s) · ${this.store.reasonName(c.reason_id)}${comment}`,
      stamp: stampText(this.store, c.created),
      cancelled: c.cancelled ? cancelledText(this.store, c.cancelled) : null,
      replaces: c.replaces ?? null,
      live: !c.cancelled,
    };
  }

  private qty(): number | null {
    const t = this.qtyText.trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }

  private errorsOf(): string[] {
    const out: string[] = [];
    const q = this.qty();
    if (this.badQty) out.push('Quantité non comprise.');
    else if (this.qtyText.trim() && !(q !== null && q > 0)) out.push('La quantité annulée est positive.');
    if (this.commentRequired && !this.comment.trim()) out.push('Commentaire obligatoire pour ce motif.');
    return out;
  }

  // Aucun plafond ici : au-delà du livré, le serveur refuse avec son message, affiché tel quel.
  // seen : nombre d'annulations affichées (annulées comprises) ; un second envoi de la même saisie est refusé par le serveur.
  private event(): RentalDecisionEvent | null {
    const q = this.qty();
    if (this.blocked || !this.sale || q === null || !(q > 0) || this.reasonId === null) return null;
    return saleCancellationAdd(this.data.orderId, this.data.lineId, q, this.reasonId, this.comment, this.sale.cancellations?.length ?? 0);
  }

  setQty(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    this.qtyText = input.value;
    this.badQty = !!input.validity?.badInput;
    this.update();
  }

  pickReason(id: number): void {
    this.reasonId = id;
    this.update();
  }

  setComment(v: string): void {
    this.comment = v;
    this.update();
  }

  cancel(id: number): void {
    const c = this.sale?.cancellations?.find((x) => x.id === id);
    if (!c) return;
    this.nested(this.dialogs.openCancelDecision({
      store: this.store, orderId: this.data.orderId, typ: EventType.RENTAL_SALE_CANCELLATION,
      targets: [{ lineId: this.data.lineId, id: c.id, checked: true }],
      title: "Annuler l'annulation de quantité",
      summary: `Annulation n° ${c.id} : ${qtyFr(c.qty)} × ${this.name} · ${this.store.reasonName(c.reason_id)}`,
    }));
  }

  save(): void {
    const ev = this.canSave ? this.event() : null;
    if (ev) this.send([ev], 'Annulation de quantité');
  }
}
